import { Button, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { LIMIT } from 'modules/orders/ordersSlice';
import React, { FC } from 'react';
import { CaretLeft, CaretRight } from 'components/Icons/Icons';
import colors from 'themes/default/common/colors';
import { mq } from 'styles/breakpoints';
import { rem } from 'utils/fonts';

const Wrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  color: ${colors.gray[900]};
  margin-bottom: 55px;
  span {
    opacity: 0.5;
  }

  ${mq.md} {
    justify-content: flex-end;
  }
`;

const StyledArrowLeft = styled(CaretLeft)`
  color: ${colors.gray[900]};
  cursor: pointer;
  margin-bottom: 4px;
  font-size: ${rem(22)};
`;

const StyledArrowRight = styled(CaretRight)`
  color: ${colors.gray[900]};
  cursor: pointer;
  margin-bottom: 4px;
  font-size: ${rem(22)};
`;

const StyledButton = styled(Button)`
  font-size: ${rem(14)};
  color: ${colors.gray[900]};
  font-weight: bold;
  padding: 0 5px;

  &:hover {
    background: transparent;
  }
`;

interface Props {
  currentPage: number;
  onPageChange: (page: number) => void;
  totalItems: number;
}

const Pagination: FC<Props> = ({ currentPage, onPageChange, totalItems }) => {
  const currentPageIdx = currentPage - 1;
  const pages = Math.ceil(totalItems / LIMIT);
  const maxVisiblePages = 5;
  const isNearStart = currentPageIdx - maxVisiblePages < 0;

  const isMoreThanDoubleMaxPages = pages - maxVisiblePages >= maxVisiblePages;
  const isTheLastVisiblePages = currentPageIdx + maxVisiblePages > pages;
  const isMoreThanMaxPages = pages > maxVisiblePages;
  const isNearEnd = isMoreThanDoubleMaxPages ? isTheLastVisiblePages : isMoreThanMaxPages ? !isNearStart : true;

  const rangeStart = isNearStart ? 0 : isNearEnd ? pages - maxVisiblePages : currentPageIdx - 2;
  const rangeEnd = isNearStart ? maxVisiblePages : isNearEnd ? pages : currentPageIdx + 3;

  return pages > 1 ? (
    <Wrapper>
      {currentPageIdx > 0 && <StyledArrowLeft aria-label="Go to previous page." onClick={(): void => onPageChange(currentPage - 1)} />}
      {!isNearStart && (
        <>
          <StyledButton aria-label="Page 1." variant="ghost" opacity={0.5} onClick={(): void => onPageChange(1)}>
            1
          </StyledButton>
          <span>...</span>
        </>
      )}
      {Array.from(Array(pages).keys())
        .slice(rangeStart, rangeEnd)
        .map(p => (
          <StyledButton
            aria-label={`Page ${p + 1}${currentPageIdx === p ? ', Current page.' : '.'}`}
            variant="ghost"
            colorScheme="gray"
            opacity={currentPageIdx === p ? 1 : 0.5}
            key={p}
            onClick={(): void => onPageChange(p + 1)}
          >
            {p + 1}
          </StyledButton>
        ))}
      {!isNearEnd && (
        <>
          <span>...</span>
          <StyledButton aria-label={`Page ${pages}.`} variant="ghost" opacity={0.5} onClick={(): void => onPageChange(pages)}>
            {pages}
          </StyledButton>
        </>
      )}
      {currentPageIdx < pages - 1 && <StyledArrowRight aria-label="Go to next page." onClick={(): void => onPageChange(currentPage + 1)} />}
    </Wrapper>
  ) : null;
};

export default Pagination;
